import _ from "lodash";
import React, { useRef, useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { withTranslation, WithTranslation } from "react-i18next";
import { Element } from "react-scroll";
import Fade from "react-reveal/Fade";
import MediaQuery from "react-responsive";
import Swiper from "react-id-swiper";

import { H2Pre, Body1Pre, Button, H4 } from "src/components/Typography";
import { colors } from "src/styles/colors";
import { media, desktopWidth, mobileWidth } from "src/utils/media";
import { translate } from "src/locales";
import images from "src/images";
import UsecaseWalletServiceCard from "src/components/card/UsecaseWalletServiceCard";
import UsecaseExchange2ServiceCard from "src/components/card/UsecaseExchange2ServiceCard";
import UsecaseExchangeServiceCard from "src/components/card/UsecaseExchangeServiceCard";
import UsecaseConsignmentServiceCard from "src/components/card/UsecaseConsignmentServiceCard";
import UsecasePaymentServiceCard from "src/components/card/UsecasePaymentServiceCard";
import { gTagEvent, ActionType, CategoryType } from "src/configs/analytics";
import { getLinksByLanguage } from "src/constants/link";

type Props = { isKo: boolean } & WithTranslation;

const Container = styled(Element)`
  display: flex;
  background-color: ${colors.bg.white.main};
  min-width: 1440px;
  height: 1324px;
  padding-top: 200px;
  padding-bottom: 200px;
  ${media.mobile`
    min-width: auto;
    height: auto;
    padding-top: 108px;
    padding-bottom: 108px;
  `}
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  width: 1352px;
  margin-left: auto;
  margin-right: auto;
  ${media.mobile`
    position: relative;
    width: 100%;
  `}
`;

const TabLabelGroup = styled.div`
  width: auto;
  display: flex;
  flex-direction: row;
  ${media.desktop`
    padding-left: 40px;
    padding-right: 40px;
  `}
  ${media.mobile`
    width: 330px;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 40px;
    margin-left: auto;
    margin-right: auto;
  `}
`;

const LastTabLabel = styled(H4)<{ $active: boolean }>`
  position: relative;
  height: 37px;
  cursor: pointer;
  ${media.mobile`
    margin-left: 10px;
    margin-right: 10px;
    margin-top: 4px;
  `}
  ${({ $active }) =>
    $active
      ? css`
          color: ${colors.text.dark.title};
        `
      : css`
          color: ${colors.text.dark.sub};
        `}
`;

const TabLabel = styled(LastTabLabel)`
  margin-right: 32px;
  ${media.mobile`
    margin-left: 10px;
    margin-right: 10px;
    margin-top: 4px;
  `}
`;

const TabLabelUnderline = styled.div`
  position: absolute;
  width: 100%;
  height: 3px;
  margin-top: 6px;
  background-color: ${colors.primary.default};
  ${media.mobile`
    height: 2px;
  `}
`;

const IndicatorGroup = styled.div`
  display: flex;
  flex-direction: row;
  width: 104px;
  justify-content: space-between;
  align-self: center;
`;

const IndicatorIcon = styled.img<{ $active: boolean }>`
  width: 42px;
  height: 42px;
  transition: all 0.15s linear 0s;
  cursor: pointer;
  ${({ $active }) =>
    $active
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0.2;
          cursor: default;
        `}
`;

const SliderGroup = styled.div`
  display: flex;
  flex-direction: row;
  ${media.desktop`
    height: 829px;
    padding-top: 120px;
  `}
`;

const UsecaseItem = styled.div`
  margin-left: auto;
  margin-right: auto;
`;

const UsecaseItemContent = styled.div`
  ${media.desktop`
    padding-left: 40px;
    padding-right: 40px;
    height: calc(829px - 120px);
    background-color: #fff;
  `}
`;

const UsecasesSection = (props: Props) => {
  const { isKo, i18n, t } = props;
  const [slideIndex, setSlideIndex] = useState(0);
  const swiperRef = useRef<any | null>(null);
  const language = i18n.language;
  const slideParams = {
    allowTouchMove: false,
    autoHeight: true,
    effect: "fade",
  };

  const usecaseItemMap: Record<
    | "usecaseWalletService"
    | "usecaseExchange2Service"
    | "usecaseConsignmentService"
    | "usecasePaymentService",
    { key: string; name: string; Component: React.ReactNode }
  > = {
    usecaseWalletService: {
      key: "usecaseWalletService",
      name: translate(["usecasesSection", "walletTabLabel"], t),
      Component: (
        <UsecaseWalletServiceCard
          isKo={isKo}
          onLink={() => {
            window.open(
              getLinksByLanguage(language).home.usecasesSection
                .usecaseWalletService
            );
          }}
        />
      ),
    },
    usecaseExchange2Service: {
      key: "usecaseExchange2Service",
      name: translate(["usecasesSection", "exchange2TabLabel"], t),
      Component: (
        <UsecaseExchange2ServiceCard
          isKo={isKo}
          onLink={() => {
            window.open(
              isKo
                ? getLinksByLanguage(language).home.usecasesSection
                    .usecaseExchange2Service
                : getLinksByLanguage(language).home.usecasesSection
                    .usecaseExchange2EnService
            );
          }}
        />
      ),
    },
    usecaseConsignmentService: {
      key: "usecaseConsignmentService",
      name: translate(["usecasesSection", "consignmentTabLabel"], t),
      Component: (
        <UsecaseConsignmentServiceCard
          onLink={() => {
            window.open(
              isKo
                ? getLinksByLanguage(language).home.usecasesSection
                    .usecaseConsignmentService
                : getLinksByLanguage(language).home.usecasesSection
                    .usecaseConsignmentEnService
            );
          }}
        />
      ),
    },
    usecasePaymentService: {
      key: "usecasePaymentService",
      name: translate(["usecasesSection", "paymentTabLabel"], t),
      Component: (
        <UsecasePaymentServiceCard
          onLink={() => {
            window.open(
              getLinksByLanguage(language).home.usecasesSection
                .usecasePaymentService
            );
          }}
        />
      ),
    },
  };
  const koUsecaseItems = [
    usecaseItemMap["usecaseWalletService"],
    usecaseItemMap["usecaseExchange2Service"],
    usecaseItemMap["usecaseConsignmentService"],
    usecaseItemMap["usecasePaymentService"],
  ];
  const enUsecaseItems = [
    usecaseItemMap["usecaseExchange2Service"],
    usecaseItemMap["usecaseConsignmentService"],
    usecaseItemMap["usecaseWalletService"],
    usecaseItemMap["usecasePaymentService"],
  ];
  const usecaseItems = isKo ? koUsecaseItems : enUsecaseItems;

  const onChangeIndex = (index: number) => {
    setSlideIndex(index);
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideTo(index);
    }
  };

  const next = () => {
    if (slideIndex === usecaseItems.length - 1) {
      return;
    }
    setSlideIndex(slideIndex + 1);
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();
    }
  };

  const prev = () => {
    if (slideIndex === 0) {
      return;
    }
    setSlideIndex(slideIndex - 1);
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  const TabsComponent = (
    <TabLabelGroup>
      {usecaseItems.map((item, index) => {
        const { key, name } = item;
        if (index === usecaseItems.length - 1) {
          return (
            <LastTabLabel
              key={key}
              $active={slideIndex === index}
              onClick={_.partial(onChangeIndex, index)}
            >
              {name}
              {slideIndex === index ? <TabLabelUnderline /> : null}
            </LastTabLabel>
          );
        }
        return (
          <TabLabel
            key={key}
            $active={slideIndex === index}
            onClick={_.partial(onChangeIndex, index)}
          >
            {name}
            {slideIndex === index ? <TabLabelUnderline /> : null}
          </TabLabel>
        );
      })}
    </TabLabelGroup>
  );

  return (
    <Container id="usecases" name="usecases">
      <Inner>
        {TabsComponent}
        <SliderGroup>
          <Swiper ref={swiperRef} {...slideParams}>
            {usecaseItems.map((item, index) => {
              const { key, Component } = item;
              return (
                <UsecaseItem key={`${key}${index}`}>
                  <UsecaseItemContent>{Component}</UsecaseItemContent>
                </UsecaseItem>
              );
            })}
          </Swiper>
        </SliderGroup>
        <MediaQuery minWidth={desktopWidth}>
          <IndicatorGroup>
            <IndicatorIcon
              $active={slideIndex !== 0}
              src={images.carouselArrowLeftSrc}
              srcSet={images.carouselArrowLeft}
              onClick={prev}
            />
            <IndicatorIcon
              $active={slideIndex !== usecaseItems.length - 1}
              src={images.carouselArrowRightSrc}
              srcSet={images.carouselArrowRight}
              onClick={next}
            />
          </IndicatorGroup>
        </MediaQuery>
      </Inner>
    </Container>
  );
};

export default withTranslation()(UsecasesSection);
